import { faAngleDown, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useRef, useState } from 'react'

export type DropDownItem = {
    id: string
    value: React.ReactNode
}

type Props = {
    items: DropDownItem[]
    label: string
    selectedItemIds: string[]
    onSelectionChange: (ids: string[]) => void
    containerClasses?: string
}

export default function MultiSelectDropdown({
    items,
    label,
    selectedItemIds,
    onSelectionChange,
    containerClasses,
}: Props): React.JSX.Element {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const toggleDropdown = () => setIsOpen((prev) => !prev)

    const handleItemSelected = (itemId: string) => {
        if (!selectedItemIds.includes(itemId)) {
            onSelectionChange([...selectedItemIds, itemId])
        }
        // setIsOpen(false)
    }

    const handleRemoveSelected = (itemId: string) => {
        onSelectionChange(selectedItemIds.filter((id) => id !== itemId))
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const availableItems = items.filter(
        (item) => !selectedItemIds.includes(item.id),
    )
    const selectedItems = items.filter((item) =>
        selectedItemIds.includes(item.id),
    )

    return (
        <div className={containerClasses}>
            <div className="relative" ref={containerRef}>
                <div
                    onClick={toggleDropdown}
                    className={
                        'border border-gray-300 rounded-lg px-2 bg-white hover:border-ctxt-blue hover:bg-gray-100 cursor-pointer min-h-14 pb-2'
                    }
                >
                    {selectedItems.length > 0 && (
                        <div className="text-gray-700 text-xs mb-1 mt-1">
                            {label}
                        </div>
                    )}

                    <div className="flex justify-between items-center gap-2 flex-wrap">
                        <div className="flex flex-wrap items-center gap-1">
                            {selectedItems.length > 0 ? (
                                selectedItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className="flex items-center text-sm bg-gray-200 rounded-lg px-1 py-0.5 gap-1 mx-1 pr-2"
                                    >
                                        {item.value}
                                        <button
                                            className="hover:text-red-500"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleRemoveSelected(item.id)
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                size="sm"
                                            />
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="text-sm text-gray-900">
                                    {label}
                                </div>
                            )}
                        </div>

                        <div className="shrink-0">
                            <FontAwesomeIcon
                                icon={faAngleDown}
                                className="transition-transform duration-200"
                                style={{
                                    transform: isOpen
                                        ? 'rotate(180deg)'
                                        : 'rotate(0deg)',
                                }}
                            />
                        </div>
                    </div>
                </div>

                {isOpen && (
                    <div className="absolute left-0 top-full w-auto min-w-[150px] bg-white border border-gray-300 shadow-lg rounded-md z-50 max-h-60 overflow-y-auto">
                        {/* Controls */}
                        <div className="flex justify-between px-2 py-1 border-b border-gray-200 text-xs text-ctxt-blue font-medium">
                            <button
                                className="hover:underline"
                                onClick={() => {
                                    onSelectionChange(
                                        items.map((item) => item.id),
                                    )
                                    setIsOpen(false)
                                }}
                            >
                                Select All
                            </button>
                            <button
                                className="hover:underline text-red-500"
                                onClick={() => {
                                    onSelectionChange([])
                                    // setIsOpen(false)
                                }}
                            >
                                Clear
                            </button>
                        </div>

                        {/* Items */}
                        {availableItems.map((item) => (
                            <div
                                key={item.id}
                                className="cursor-pointer hover:bg-ctxt-blue-light hover:bg-opacity-10 px-2 py-1"
                                onClick={() => handleItemSelected(item.id)}
                            >
                                <div className="text-sm text-gray-900">
                                    {item.value}
                                </div>
                            </div>
                        ))}

                        {/* No more items */}
                        {availableItems.length === 0 && (
                            <div className="text-sm text-gray-500 px-2 py-1">
                                No more items
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
